import React, { useState } from 'react';
import { HiChevronDown } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import './FAQ.css';

const faqCategories = [
  {
    category: 'General',
    questions: [
      {
        q: 'What is AIScaler?',
        a: 'AIScaler is an AI-powered platform that lets you upscale and enhance images, videos, and documents automatically. Using state-of-the-art neural networks, it can increase resolution, reduce noise, restore faces, sharpen text, and much more.',
      },
      {
        q: 'What file types does AIScaler support?',
        a: 'AIScaler supports a wide range of formats including JPG, PNG, WebP for images, MP4 for videos, and PDF for documents. You can also export in multiple formats including social media, print, and web-optimized presets.',
      },
      {
        q: 'Do I need any technical skills to use AIScaler?',
        a: 'Not at all! AIScaler is designed to be intuitive. Simply drag and drop your files, and our AI automatically detects the content type and applies the optimal enhancement. For advanced users, custom tuning sliders are available.',
      },
    ],
  },
  {
    category: 'Image Enhancement',
    questions: [
      {
        q: 'How much can I upscale an image?',
        a: 'AIScaler supports 2x, 4x, and 8x upscaling. The AI adds realistic detail and sharpness while preserving the original character of the image.',
      },
      {
        q: 'Can AIScaler restore old or damaged photos?',
        a: 'Yes! Our Old Photo Restore mode can fix scratches, fading, color shifts, and noise in vintage photographs. It also includes face restoration that can reconstruct facial features with remarkable accuracy.',
      },
      {
        q: 'What is Face Restoration?',
        a: 'Face Restoration automatically detects faces in your images and enhances them — sharpening features, smoothing skin, and restoring details that may have been lost due to low resolution or compression.',
      },
    ],
  },
  {
    category: 'Video Processing',
    questions: [
      {
        q: 'What video resolutions can AIScaler produce?',
        a: 'AIScaler can upscale videos to 1080p, 4K, and even 8K resolution. It also includes frame interpolation to boost frame rates from 30fps to 60fps or 120fps for smoother playback.',
      },
      {
        q: 'How long does video processing take?',
        a: 'Processing time depends on the video length, resolution, and selected enhancements. Our GPU-accelerated pipeline processes videos significantly faster than traditional methods. You can track progress in real-time with our queue system.',
      },
    ],
  },
  {
    category: 'API & Integration',
    questions: [
      {
        q: 'Does AIScaler offer an API?',
        a: 'Yes! Our REST API supports image enhancement, video processing, and document cleanup. We also provide SDKs for Node.js and Python, along with webhooks for job completion notifications.',
      },
      {
        q: 'What integrations are available?',
        a: 'AIScaler integrates with Shopify (auto-enhance product images), Canva, Figma, Google Drive, Dropbox, and Zapier. More integrations are being added regularly.',
      },
    ],
  },
  {
    category: 'Pricing & Billing',
    questions: [
      {
        q: 'Is there a free plan?',
        a: 'Yes! Our free plan includes 5 images per day with up to 2x upscaling and basic noise reduction. No credit card required.',
      },
      {
        q: 'How does the credit system work?',
        a: 'Credits are consumed per processing job. The number of credits depends on the file type, size, and enhancement level. Pro plans include 500 credits/month, and Business plans offer unlimited credits.',
      },
      {
        q: 'Can I cancel my subscription anytime?',
        a: 'Absolutely. You can cancel your subscription at any time from your account settings. Your access continues until the end of the current billing period.',
      },
    ],
  },
  {
    category: 'Privacy & Security',
    questions: [
      {
        q: 'Is my data safe with AIScaler?',
        a: 'Yes. We use secure file storage, and all files are auto-deleted after processing (configurable retention period). We never use your uploads to train our AI models. AIScaler is GDPR-ready.',
      },
      {
        q: 'Where are files processed?',
        a: 'Files are processed on our secure GPU infrastructure. Outputs are delivered via CDN for fast downloads. We support multi-region processing for enterprise customers.',
      },
    ],
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={`faq-item ${open ? 'faq-item--open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="faq-item__question">
        <span>{q}</span>
        <HiChevronDown className="faq-item__chevron" />
      </div>
      <div className="faq-item__answer">
        <p>{a}</p>
      </div>
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="faq-page">
      <section className="page-hero">
        <div className="container">
          <span className="section-label">FAQ</span>
          <h1 className="section-title">Frequently Asked Questions</h1>
          <p className="section-subtitle">
            Everything you need to know about AIScaler. Can't find your answer? Contact us.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="faq-content">
            {faqCategories.map(({ category, questions }) => (
              <div key={category} className="faq-category">
                <h2 className="faq-category__title">{category}</h2>
                <div className="faq-category__items">
                  {questions.map(({ q, a }) => (
                    <FAQItem key={q} q={q} a={a} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section faq-cta">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="section-title">Still Have Questions?</h2>
          <p className="section-subtitle" style={{ margin: '0 auto 32px' }}>
            Our team is happy to help. Reach out anytime.
          </p>
          <Link to="/contact" className="btn btn-primary">Contact Us</Link>
        </div>
      </section>
    </div>
  );
}
